import { ChordLib, NoteLib, isChordVariant } from '@tunasong/music-lib'
import type { Chord, ModulationPlan, VoicedChord } from '@tunasong/music-lib'
import type { NoteName } from '@tunasong/schemas'
import { chordToEvents, voiceLeadingChordToEvents } from './chord-to-events.js'
import type { Stab } from './stab-scheduler.js'

/**
 * Modulation Lab audition primitive. Pure function — emits a chronologically
 * ordered list of `Stab`s for the 3-phase modulation contract (source vamp →
 * modulation phase → destination vamp). Parallel to `section-sequencer.ts` —
 * factored separately so the Modulation Lab's phase typing stays explicit.
 *
 * Per-phase timing model (100 BPM):
 *  - Each measure = 4 quarters × (60000/bpm) = 2400ms at 100 BPM.
 *  - A section's bar count = the length of its progression (v1.5 short audition
 *    uses 2-bar vamps; standard uses 4-bar vamps; the mod phase is always 4).
 *  - 250ms inter-phase gap (50ms pre + 200ms post on the boundary —
 *    same model as `section-sequencer.ts`).
 *
 * Parallel-mode technique (Bowie §8.4): the modulation-phase chords play with
 * the THIRD in the top (soprano) voice — that 3rd is the chromatic line the
 * listener attends to (E → E♭ for C major → C minor). The voicing is
 * [root@4, fifth@(4|5), third@5] emitted bass→soprano, so the soprano is the
 * 3rd regardless of chord quality. Source and destination vamps stay at the
 * default octave 4 with the plain root-position voicing.
 *
 * Spec: docs/superpowers/specs/2026-06-02-modulation-lab-v15-design.md §Track 1
 */

const QUARTERS_PER_MEASURE = 4
const PRE_ROLL_MS = 50
const POST_ROLL_MS = 200
const SECTION_GAP_MS = PRE_ROLL_MS + POST_ROLL_MS

const CHORD_DURATION = '2n' as const
const DEFAULT_OCTAVE = 4

/**
 * Build the parallel-mode voicing for a single chord: bass = root@4, then fifth
 * and third stacked so the THIRD is the highest-SOUNDING voice — the chromatic
 * line the listener tracks (E → E♭ for C major → C minor; C → C♯ for A minor →
 * A major). Octaves are assigned by absolute pitch (octave×12 + chromatic
 * index): fifth = lowest octave strictly above the root, third = lowest octave
 * strictly above the fifth. This guarantees root < fifth < third for every
 * chord quality — a fixed third@5 would let a minor source's 5th (e.g. Am's
 * E5) rise above its 3rd (C5), burying the diagnostic line (the §8.4 defect).
 * Generic across qualities because third/fifth are picked by interval-from-root,
 * not array position (ChordLib.getNotes returns chromatically-sorted notes).
 */
const buildParallelVoicing = (chord: Chord): VoicedChord => {
  const notes = ChordLib.getNotes(chord)
  const rootPc = isChordVariant(chord)
    ? NoteLib.getNoteChromaticIndex(chord.root)
    : NoteLib.getNoteChromaticIndex(notes[0])
  const offsetFromRoot = (n: NoteName): number =>
    (NoteLib.getNoteChromaticIndex(n) - rootPc + 12) % 12
  const root = notes.find(n => offsetFromRoot(n) === 0) ?? notes[0]
  const third = notes.find(n => offsetFromRoot(n) === 3 || offsetFromRoot(n) === 4) ?? notes[1]
  const fifth =
    notes.find(n => {
      const o = offsetFromRoot(n)
      return o === 6 || o === 7 || o === 8
    }) ?? notes[2]
  const absPitch = (n: NoteName, octave: number): number =>
    octave * 12 + NoteLib.getNoteChromaticIndex(n)
  const rootAbs = absPitch(root, DEFAULT_OCTAVE)
  let fifthOctave = DEFAULT_OCTAVE
  while (absPitch(fifth, fifthOctave) <= rootAbs) {
    fifthOctave++
  }
  let thirdOctave = DEFAULT_OCTAVE
  while (absPitch(third, thirdOctave) <= absPitch(fifth, fifthOctave)) {
    thirdOctave++
  }
  return {
    pitches: [
      `${root}${DEFAULT_OCTAVE}`,
      `${fifth}${fifthOctave}`,
      `${third}${thirdOctave}`,
    ] as unknown as NoteName[],
  }
}

export interface ModulationSequencerInput {
  plan: ModulationPlan
  /** BPM — v1 locks this at 100. */
  bpm: number
}

export interface ModulationPhaseBoundaries {
  sourceStartMs: number
  modulationStartMs: number
  destinationStartMs: number
  endMs: number
}

export interface ModulationStabsResult {
  stabs: Stab[]
  phaseBoundaries: ModulationPhaseBoundaries
}

/** Pure stab emitter. Deterministic given `plan` + `bpm`. */
export const buildModulationStabs = (input: ModulationSequencerInput): ModulationStabsResult => {
  const { plan, bpm } = input
  const quarterMs = 60000 / bpm
  const measureMs = QUARTERS_PER_MEASURE * quarterMs

  const stabs: Stab[] = []

  // Each chord occupies one measure; a section's duration = bars × measureMs.
  const sectionDurationMs = (chords: ModulationPlan['sourceProgression']): number =>
    chords.length * measureMs

  const emitSection = (
    chords: ModulationPlan['sourceProgression'],
    startMs: number,
    parallel: boolean
  ): void => {
    for (let i = 0; i < chords.length; i++) {
      const events = parallel
        ? voiceLeadingChordToEvents(buildParallelVoicing(chords[i]), CHORD_DURATION)
        : chordToEvents(chords[i], CHORD_DURATION, DEFAULT_OCTAVE)
      stabs.push({ events, delayMs: startMs + i * measureMs })
    }
  }

  const sourceStartMs = 0
  emitSection(plan.sourceProgression, sourceStartMs, false)

  const modulationStartMs = sourceStartMs + sectionDurationMs(plan.sourceProgression) + SECTION_GAP_MS
  emitSection(plan.modulationProgression, modulationStartMs, plan.technique === 'parallel')

  const destinationStartMs =
    modulationStartMs + sectionDurationMs(plan.modulationProgression) + SECTION_GAP_MS
  emitSection(plan.destinationProgression, destinationStartMs, false)

  return {
    stabs,
    phaseBoundaries: {
      sourceStartMs,
      modulationStartMs,
      destinationStartMs,
      endMs: destinationStartMs + sectionDurationMs(plan.destinationProgression),
    },
  }
}
